// RuleConditionFields — source filter, prior-conversation tri-state, min nights.

import { useId } from 'react';
import { MultiSelectFilter, type FilterOption } from '@/components/ui/multi-select-filter';
import { Input } from '@/components/ui/input';

export interface ConditionsFormState {
  sources: Set<string>;
  hadPriorConversation: boolean | undefined; // undefined = Any, true = Only with, false = Only without
  minNights: string; // string for controlled input; '' = not set
}

interface RuleConditionFieldsProps {
  conditions: ConditionsFormState;
  onChange: (c: ConditionsFormState) => void;
  disabled?: boolean;
}

const SOURCE_OPTIONS: FilterOption[] = [
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'booking_com', label: 'Booking.com' },
  { value: 'direct', label: 'Direct' },
  { value: 'manual', label: 'Manual' },
];

interface PriorConvOption { value: string; label: string; resolved: boolean | undefined }
const PRIOR_CONV_OPTIONS: PriorConvOption[] = [
  { value: 'any', label: 'Any', resolved: undefined },
  { value: 'with', label: 'Only with prior conversation', resolved: true },
  { value: 'without', label: 'Only without prior conversation', resolved: false },
];

export function RuleConditionFields({ conditions, onChange, disabled }: RuleConditionFieldsProps) {
  const priorId = useId();

  const priorValue =
    conditions.hadPriorConversation === undefined
      ? 'any'
      : conditions.hadPriorConversation
        ? 'with'
        : 'without';

  return (
    <div className="flex flex-col gap-4">
      {/* Source */}
      <div className="flex flex-col gap-1">
        <span className="text-sm font-semibold text-[var(--text-heading)]">Booking source</span>
        <MultiSelectFilter
          label="Source"
          options={SOURCE_OPTIONS}
          selected={conditions.sources}
          onChange={(next) => onChange({ ...conditions, sources: next })}
          searchable={false}
        />
        {conditions.sources.size === 0 && (
          <span className="text-xs text-muted">All sources (no filter)</span>
        )}
      </div>

      {/* Prior conversation tri-state */}
      <div className="flex flex-col gap-1">
        <label htmlFor={priorId} className="text-sm font-semibold text-[var(--text-heading)]">
          Prior conversation
        </label>
        <select
          id={priorId}
          value={priorValue}
          onChange={(e) => {
            const opt = PRIOR_CONV_OPTIONS.find((o) => o.value === e.target.value);
            onChange({ ...conditions, hadPriorConversation: opt?.resolved });
          }}
          disabled={disabled}
          className="w-full appearance-none cursor-pointer rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)] disabled:opacity-55"
        >
          {PRIOR_CONV_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* Min nights */}
      <Input
        label="Minimum nights"
        type="number"
        min={1}
        step={1}
        value={conditions.minNights}
        onChange={(e) => onChange({ ...conditions, minNights: (e.target as HTMLInputElement).value })}
        disabled={disabled}
        placeholder="No minimum"
        hint="Leave blank to apply to all stays."
        className="max-w-xs"
      />
    </div>
  );
}
